import { useState } from 'react';

import { InputAdornment, Stack, styled, SxProps, Typography, useTheme } from '@mui/material';
import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import VectorImage from '~components/media/vectorImage';
import { findPhoneCountryByPhone, phoneCountries } from '~constants/phoneCountries';
import { BaseReference } from '~interfaces';
import i18n from '~lib/i18n';

import Dimens from '../../themes/dimens';

interface PhoneNumberDropdownProps {
	onValueChanged: (newValue?: BaseReference) => void;
	inputValue?: BaseReference;
	label?: string;
	error?: string;
	sx?: SxProps;
}

const StyledTextField = styled(TextField)(({ theme }) => ({
	'& .MuiInputBase': {
		'&-root': {
			padding: 0,
			outline: 'none',
			alignItems: 'center',
		},
		'&-input': {
			...theme.typography.paragraph,
			color: theme.palette.textTertiary.main,
			padding: `${Dimens.spacingMd} ${Dimens.spacingLg} ${Dimens.spacingMd} 0`,
			minHeight: '24px',
			'&::placeholder': {
				color: theme.palette.textPlaceholder.main,
				opacity: 1,
			},
		},
	},
	'& .MuiOutlinedInput-root': {
		'& .MuiOutlinedInput-notchedOutline': {
			top: 0,

			border: `1px solid ${theme.palette.borderPrimary.main}`,
			'& legend': {
				// This is the mui label that places itself in the border.
				// But it leaves a gap in the border, so we hide it.
				display: 'none',
			},
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			border: `2px solid ${theme.palette.borderPrimarySolid.main}`,
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			border: `2px solid ${theme.palette.borderPrimarySolid.main}`,
		},
		'&.Mui-error .MuiOutlinedInput-notchedOutline': {
			border: `2px solid ${theme.palette.error.main}`,
		},
	},
	'& .MuiInputAdornment-root': {
		marginRight: Dimens.spacingMd,
		maxHeight: 'unset',
	},
}));

const PhoneNumberDropdown = ({
	inputValue,
	label = i18n.t('user.fields.phone', { ns: 'entities' }),
	error,
	sx,
	onValueChanged,
}: PhoneNumberDropdownProps) => {
	const { typography } = useTheme();
	const [fallbackCountry, setFallbackCountry] = useState(phoneCountries[0]);
	const selectedCountry = findPhoneCountryByPhone(inputValue?.id) ?? fallbackCountry;

	const handleInputChange = (
		_event: React.SyntheticEvent,
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		const foundCountry = phoneCountries.find((country) => country.label === value);
		switch (reason) {
			case 'selectOption':
				setFallbackCountry(foundCountry ?? phoneCountries[0]);
				onValueChanged({ id: foundCountry?.phone ?? '', name: inputValue?.name ?? '' });
				break;
			case 'input':
				onValueChanged({ id: selectedCountry.phone, name: value });
				break;
			case 'reset':
				if (inputValue?.id) {
					setFallbackCountry(findPhoneCountryByPhone(inputValue.id) ?? phoneCountries[0]);
				}
				break;
		}
	};

	return (
		<Stack sx={{ gap: Dimens.spacingXs }}>
			{label && <Typography variant='labelSm'>{label}</Typography>}
			<Box>
				<Autocomplete
					id='phone-country'
					sx={{ width: 1, ...sx }}
					options={phoneCountries}
					disableClearable={true}
					autoHighlight
					inputMode='tel'
					inputValue={inputValue?.name ?? ''}
					popupIcon={
						<VectorImage
							name='chevron/down'
							style={{
								height: '16px',
								width: '16px',
								lineHeight: '24px',
							}}
						/>
					}
					filterOptions={(options) => options}
					onInputChange={(
						e: React.SyntheticEvent,
						value: string,
						reason: AutocompleteInputChangeReason
					) => {
						handleInputChange(e, value, reason);
					}}
					getOptionLabel={(option) => option.label}
					renderOption={(props, option) => {
						const { key, ...optionProps } = props;
						return (
							<Box
								key={key}
								component='li'
								sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
								{...optionProps}
							>
								<img
									loading='lazy'
									width='20'
									srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
									src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
									alt=''
								/>
								{option.label} ({option.code}) +{option.phone}
							</Box>
						);
					}}
					renderInput={(params) => (
						<StyledTextField
							{...params}
							placeholder='12345678'
							helperText={error}
							error={(error?.length ?? 0) > 0}
							slotProps={{
								input: {
									...params.InputProps,
									startAdornment: (
										<InputAdornment
											position='start'
											sx={{
												marginLeft: Dimens.spacingLg,
												whiteSpace: 'nowrap',
												flexShrink: 0,
											}}
										>
											<Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
												<img
													loading='lazy'
													width='24'
													height='18'
													srcSet={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png 2x`}
													src={`https://flagcdn.com/w20/${selectedCountry.code.toLowerCase()}.png`}
													alt=''
												/>
												<Typography variant='label'>+{selectedCountry.phone}</Typography>
											</Stack>
										</InputAdornment>
									),
								},
								htmlInput: {
									...params.inputProps,
									sx: { ...typography.label },
									autoComplete: 'new-password', // disable autocomplete and autofill
								},
							}}
						/>
					)}
				/>
			</Box>
		</Stack>
	);
};

export default PhoneNumberDropdown;
